import React, { useCallback, useRef, useState } from 'react';
import { useIsMobile } from '../../hooks/useIsMobile';

interface ExplorerViewProps {
  title: React.ReactNode;
  headerActions?: React.ReactNode;
  /** When omitted the sidebar panel is hidden entirely and content fills the full width. */
  sidebar?: React.ReactNode;
  content: React.ReactNode;
  contentHeader?: React.ReactNode;
  isSidebarExpanded?: boolean;
}

const MIN_SIDEBAR_WIDTH = 140;
const MAX_SIDEBAR_WIDTH = 520;

export const ExplorerView: React.FC<ExplorerViewProps> = ({ title, headerActions, sidebar, content, contentHeader, isSidebarExpanded = false }) => {
  const { isMobile } = useIsMobile();
  const [sidebarWidth, setSidebarWidth] = useState(() => Math.round(window.innerWidth * 0.25));
  const dragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const onResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragging.current = true;
    startX.current = e.clientX;
    startWidth.current = sidebarWidth;

    const onMove = (me: MouseEvent) => {
      if (!dragging.current) return;
      const delta = me.clientX - startX.current;
      setSidebarWidth(Math.min(MAX_SIDEBAR_WIDTH, Math.max(MIN_SIDEBAR_WIDTH, startWidth.current + delta)));
    };
    const onUp = () => {
      dragging.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [sidebarWidth]);

  return (
    <div className="explorer-view-root" style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.9) 0%, rgba(30, 41, 59, 0.8) 100%)',
      backdropFilter: 'blur(20px)',
      zIndex: 40,
      display: 'flex',
      flexDirection: 'column',
      borderTop: 'none',
      pointerEvents: 'none'
    }}>
      {/* Top Header Row (Title and Actions) */}
      <div className="explorer-view-header" style={{
        display: 'flex',
        alignItems: 'stretch',
        pointerEvents: 'auto',
        flexShrink: 0
      }}>
        {sidebar ? (
          <>
            {/* Sidebar-width title column */}
            <div className="explorer-view-title-col" style={{ alignItems: 'center', width: isMobile ? undefined : sidebarWidth }}>
              {typeof title === 'string' ? (
                <h2 className="explorer-view-title" style={{
                  margin: 0,
                  fontWeight: 300,
                  color: '#f1f5f9',
                  textShadow: '0 0 10px rgba(56, 189, 248, 0.2)'
                }}>{title}</h2>
              ) : (
                title
              )}
            </div>
            {/* Content-area header: breadcrumb left + actions right, inline with title */}
            {!isMobile && contentHeader && (
              <div className="explorer-view-content-header-inline">
                {contentHeader}
              </div>
            )}
            {/* Fallback: headerActions only (no contentHeader) */}
            {!isMobile && !contentHeader && headerActions && (
              <div className="explorer-view-header-actions" style={{
                paddingLeft: 24,
                display: 'flex',
                alignItems: 'center'
              }}>{headerActions}</div>
            )}
          </>
        ) : (
          <>
            <div className="explorer-view-title-col explorer-view-title-col--nosidebar" style={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              minWidth: 0
            }}>
              {typeof title === 'string' ? (
                <h2 className="explorer-view-title" style={{
                  margin: 0,
                  fontWeight: 300,
                  color: '#f1f5f9',
                  textShadow: '0 0 10px rgba(56, 189, 248, 0.2)'
                }}>{title}</h2>
              ) : (
                title
              )}
            </div>
            {headerActions && (
              <div className="explorer-view-header-actions explorer-view-header-actions--nosidebar" style={{
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0
              }}>{headerActions}</div>
            )}
          </>
        )}
      </div>

      {/* Bottom Row (Sidebar and Content) */}
      <div className="explorer-view-body" style={{
        flex: 1,
        display: 'flex',
        overflow: 'hidden'
      }}>
        {/* Left Sidebar — only rendered when provided */}
        {sidebar && (
          <>
            <div
              className={`explorer-view-sidebar${isSidebarExpanded ? ' explorer-view-sidebar--expanded' : ''}`}
              style={{
                display: 'flex',
                flexDirection: 'column',
                pointerEvents: 'auto',
                flexShrink: 0,
                width: isMobile ? undefined : sidebarWidth,
              }}
            >
              <div className="explorer-view-sidebar-inner" style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}>
                {sidebar}
              </div>
            </div>

            {/* Resize handle */}
            {!isMobile && (
              <div
                className="explorer-view-resize-handle"
                style={{ pointerEvents: 'auto', cursor: 'col-resize' }}
                onMouseDown={onResizeStart}
              />
            )}
          </>
        )}

        {/* Right Content */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          pointerEvents: 'auto',
          overflow: 'hidden'
        }}>
          {/* Legacy contentHeader strip for pages without sidebar */}
          {!sidebar && !isMobile && contentHeader && (
            <div style={{
              flexShrink: 0,
              padding: '8px 40px',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
            }}>
              {contentHeader}
            </div>
          )}
          <div className={`explorer-view-content-body${sidebar ? '' : ' explorer-view-content-body--nosidebar'}`} style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto'
          }}>
            {content}
          </div>
        </div>
      </div>
    </div>
  );
};
